/** @format */
/* eslint-disable @typescript-eslint/no-unused-vars */

import inViewport from 'javascript-inviewport';
import { scriptLoader } from '@services/third-party-services';

// More info at https://www.chartjs.org/

/**
 * Build Charts.
 */
export const charts = (): void => {
  /**
   * Build Donut Chart
   *
   * @param chart
   */
  const buildDoughnutChart = (chart: HTMLElement): void => {
    const data: Array<number> | undefined = chart.dataset.chartData
      ?.split(', ')
      .map((i) => Number(i));
    const colors: Array<string> | undefined =
      chart.dataset.chartColors?.split(', ');
    const labels: Array<string> | undefined =
      chart.dataset.chartLabels?.split(', ');

    if (data && colors && labels) {
      const doughnutChart = new Chart(chart, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [
            {
              data: data,
              backgroundColor: colors,
              borderColor: colors,
              hoverBackgroundColor: colors,
              hoverBorderColor: colors,
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          legend: {
            position: 'right',
            fontSize: '1.4rem',
            fontFamily: '"FF Mark", Arial',
          },
        },
      });
    }
  };

  /**
   * Parse json from script
   * @param chart
   * @returns
   */
  const parseJsonData = (chart: HTMLElement) => {
    const chartData = chart.firstChild.innerText;
    try {
      return JSON.parse(chartData);
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  /**
   * Build Bar Chart
   *
   * @param chart
   */
  const buildBarChart = async (chart: HTMLElement) => {
    const chartData = parseJsonData(chart);
    const showNumbersAsPercent = chart.dataset.showAsPercentage;
    const chartId = chart.dataset.chartId;
    const ctx = document.getElementById(chartId);

    const barChartConfig = {
      type: 'bar',
      data: {
        labels: chartData?.labels,
        datasets: chartData?.datasets,
      },
      options: {
        interaction: {
          mode: 'nearest',
        },
        scales: {
          x: {
            ticks: {
              callback: (value: number) => {
                if (showNumbersAsPercent) {
                  return `${value}%`;
                }
                return value;
              },
            },
          },
        },
        indexAxis: 'y',
        responsive: true,
        plugins: {
          tooltip: {
            titleFont: {
              size: 20,
            },
            bodyFont: {
              size: 15,
            },
            callbacks: {
              label: function (context) {
                if (showNumbersAsPercent) {
                  return `${context.dataset.label}: ${context.formattedValue}%`;
                }
                return `${context.dataset.label}: ${context.formattedValue}`;
              },
            },
          },
        },
      },
    };
    if (chartData) {
      setTimeout(() => {
        new Chart(ctx, barChartConfig);
      }, 250);
    }
  };

  /**
   * Build Line Chart
   * @param chart
   */
  const buildLineChart = (chart: HTMLElement) => {
    const chartData = parseJsonData(chart);
    const chartId = chart.dataset.chartId;
    const ctx = document.getElementById(chartId);

    const lineChartConfig = {
      type: 'line',
      data: {
        labels: chartData?.labels,
        datasets: chartData?.datasets,
      },
      options: {
        backgroundColor: '#eee',
        borderColor: '#3b60b0',
        pointBorderWidth: '3',
        radius: '5',
        hoverRadius: '5',
        responsive: true,
        plugins: {
          legend: {
            display: chartData?.datasets?.length > 1 ? true : false,
            position: 'top',
            fontSize: '1.4rem',
            fontFamily: "'FF Mark', 'Arial'",
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    };

    if (chartData) {
      setTimeout(() => {
        new Chart(ctx, lineChartConfig);
      }, 250);
    }
  };

  /**
   * Get Chart Type.
   *
   * @param animatedCharts
   */
  const getChartType = (animatedCharts: NodeListOf<HTMLElement>): void => {
    animatedCharts.forEach((chart) => {
      switch (chart.dataset.chartType) {
        case 'doughnut':
          inViewport(chart, 0.1, () => {
            buildDoughnutChart(chart);
          });
          break;
        case 'bar':
          inViewport(chart, 0.1, () => {
            buildBarChart(chart);
          });
          break;
        case 'line':
          inViewport(chart, 0.1, () => {
            buildLineChart(chart);
          });
          break;
      }
    });
  };

  /**
   * Setup Odometer.
   */
  const setupCharts = (): void => {
    const animatedCharts: NodeListOf<HTMLElement> =
      document.querySelectorAll('.animated-chart');
    if (animatedCharts.length > 0) {
      scriptLoader(
        'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.3.0/chart.min.js',
        'charts-js',
        getChartType(animatedCharts)
      );
    }
  };
  setupCharts();
};
